import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import {
    PHRASES_SELECTOR_KEYWORDS_MIN_WIDTH,
    PHRASES_SELECTOR_SELECTED_PHRASES_MIN_WIDTH,
    PHRASES_SELECTOR_START_PHRASES_MIN_WIDTH
} from '../../constants';
import { PhrasesSelectorGeneratorContainer } from '../../containers/PhrasesSelectorGeneratorContainer/PhrasesSelectorGeneratorContainer';
import { PhrasesSelectorKeywordsContainer } from '../../containers/PhrasesSelectorKeywordsContainer/PhrasesSelectorKeywordsContainer';
import { PhrasesSelectorMinusPhrasesContainer } from '../../containers/PhrasesSelectorMinusPhrasesContainer/PhrasesSelectorMinusPhrasesContainer';
import { Bem } from '../../service/Bem/Bem';
import { TestBem } from '../../service/TestBem';
import { GroupId } from '../../typings/groups';
import {
    PhrasesSelectorGroupKeywords,
    PhrasesSelectorPhrase,
    PhrasesSelectorStartPhraseNode,
    PhrasesSelectorWizardId
} from '../../typings/phrases';
import { Resizable } from '../Resizable/Resizable';
import { Tab, TabList, TabPanel, Tabs, TabsSize } from '../Tabs/Tabs';

import './PhrasesSelector.css';
import * as keyset from './PhrasesSelector.i18n';
import { PhrasesSelectorManualEntry } from './PhrasesSelectorManualEntry/PhrasesSelectorManualEntry';
import { PhrasesSelectorStartPhrases } from './PhrasesSelectorStartPhrases/PhrasesSelectorStartPhrases';
import { PhrasesSelectorToolbox } from './PhrasesSelectorToolbox/PhrasesSelectorToolbox';

const i18n = i18nFactory(keyset);

export interface PhrasesSelectorProps {
    wizardId: PhrasesSelectorWizardId;
    groupId: GroupId;
    cls?: string;

    startPhrases: Array<PhrasesSelectorStartPhraseNode>;
    keywords: Array<PhrasesSelectorPhrase>;
    groupKeywords: PhrasesSelectorGroupKeywords;
    isExcludePhrases: boolean;
    areBulkActionsDisabled: boolean;
    areMinusPhrasesEnabled: boolean;
    manualValue: string;
    startPhrasesError: string;
    onStartPhrasesChange: (nodes: Array<PhrasesSelectorStartPhraseNode>) => void;
    onKeywordAdd: (phrase: PhrasesSelectorPhrase) => void;
    onMinusPhraseAdd: (phrase: string) => void;
    onPhraseStatsFetch: (phrase: PhrasesSelectorStartPhraseNode) => void;
    onManualValueChange: (value: string) => void;
    onManualValueSubmit: () => void;
    onExcludePhrasesToggle: () => void;
    onBulkKeywordsAdd: () => void;
    onBulkStartPhrasesRemove: () => void;
    onMinusWordAdd: (word: string) => void;
}

const BLOCK = 'phrases-selector';

export class PhrasesSelector extends React.PureComponent<PhrasesSelectorProps> {
    render() {
        const {
            cls,
            isExcludePhrases,
            areBulkActionsDisabled,
            areMinusPhrasesEnabled,
            wizardId,
            groupId,
            startPhrases,
            keywords,
            groupKeywords,
            manualValue,
            startPhrasesError,
            onStartPhrasesChange,
            onKeywordAdd,
            onMinusPhraseAdd,
            onPhraseStatsFetch,
            onManualValueChange,
            onManualValueSubmit,
            onExcludePhrasesToggle,
            onBulkKeywordsAdd,
            onMinusWordAdd,
            onBulkStartPhrasesRemove
        } = this.props;

        return (
            <div className={classNames(Bem.cls(BLOCK), TestBem.cls(BLOCK), cls)}>
                <Tabs size={TabsSize.m}>
                    <TabList className={classNames(Bem.cls(BLOCK, 'tab-list'), TestBem.cls(BLOCK, 'tab-list'))}>
                        <Tab>{i18n('combinator-tab')}</Tab>
                        <Tab>{i18n('manual-input-tab')}</Tab>
                    </TabList>
                    <TabPanel className={Bem.cls(BLOCK, 'tab-panel')}>
                        <PhrasesSelectorGeneratorContainer
                            isExcludePhrases={isExcludePhrases}
                            groupId={groupId}
                            wizardId={wizardId}
                            onExcludePhrasesToggle={onExcludePhrasesToggle}
                        />
                    </TabPanel>
                    <TabPanel className={Bem.cls(BLOCK, 'tab-panel')}>
                        <PhrasesSelectorManualEntry
                            isExcludePhrases={isExcludePhrases}
                            value={manualValue}
                            onChange={onManualValueChange}
                            onSubmit={onManualValueSubmit}
                            onExcludePhrasesToggle={onExcludePhrasesToggle}
                        />
                    </TabPanel>
                </Tabs>
                <PhrasesSelectorToolbox
                    areBulkActionsDisabled={areBulkActionsDisabled}
                    onKeywordsAdd={onBulkKeywordsAdd}
                    onRemove={onBulkStartPhrasesRemove}
                />
                <div className={Bem.cls(BLOCK, 'phrases')}>
                    <Resizable
                        minLeft={PHRASES_SELECTOR_START_PHRASES_MIN_WIDTH}
                        minRight={
                            areMinusPhrasesEnabled
                                ? PHRASES_SELECTOR_SELECTED_PHRASES_MIN_WIDTH
                                : PHRASES_SELECTOR_KEYWORDS_MIN_WIDTH
                        }
                        left={() => (
                            <PhrasesSelectorStartPhrases
                                areMinusPhrasesEnabled={areMinusPhrasesEnabled}
                                keywords={keywords}
                                groupKeywords={groupKeywords}
                                startPhrases={startPhrases}
                                errorMessage={startPhrasesError}
                                onStartPhrasesChange={onStartPhrasesChange}
                                onKeywordAdd={onKeywordAdd}
                                onMinusPhraseAdd={onMinusPhraseAdd}
                                onPhraseStatsFetch={onPhraseStatsFetch}
                                onMinusWordAdd={onMinusWordAdd}
                            />
                        )}
                        right={(width: number) => (
                            <div className={Bem.cls(BLOCK, 'selected-phrases')} style={{ width: `${width * 100}%` }}>
                                <PhrasesSelectorKeywordsContainer wizardId={wizardId} groupId={groupId} />
                                {areMinusPhrasesEnabled && (
                                    <PhrasesSelectorMinusPhrasesContainer wizardId={wizardId} groupId={groupId} />
                                )}
                            </div>
                        )}
                    />
                </div>
            </div>
        );
    }
}
